import { useEffect, useState } from 'react'
import { GoogleLogo } from '@/components/ui/icons/GoogleLogo'
import { useGoogleLogin } from '@react-oauth/google'
import { Button, type ButtonProps } from '@/components/ui/shadcn/button'
import {
  trackConnectorConnectStarted,
  trackPlatformConnectStarted,
} from '@/lib/analytics/connectionTracking'

export const AUTH_STATE_STORAGE_KEY = 'google_auth_state' as const
export type AuthState = {
  rnd: string
  referrerPath: string
  redirectUri: string
  flow: 'login' | 'drive' | 'youtube'
}

type Props = {
  referrerPath: string
  flow: 'login' | 'drive' | 'youtube'
  buttonProps?: ButtonProps & { icon?: React.ReactNode }
  scope?: string[]
}

const buildAuthState = (referrerPath: string, flow: AuthState['flow']): AuthState => ({
  referrerPath,
  redirectUri: `${window.location.origin}/auth/google`,
  rnd: Math.random().toString().slice(3, 12),
  flow,
})

export const GoogleAuthButton = ({ referrerPath, flow, buttonProps, scope }: Props) => {
  const [state, setState] = useState<AuthState>(() => buildAuthState(referrerPath, flow))

  // Rebuild state when deps change
  const [prevDeps, setPrevDeps] = useState({ referrerPath, flow })
  if (prevDeps.referrerPath !== referrerPath || prevDeps.flow !== flow) {
    setPrevDeps({ referrerPath, flow })
    setState(buildAuthState(referrerPath, flow))
  }

  // Sync to localStorage (side effect only)
  useEffect(() => {
    localStorage.setItem(AUTH_STATE_STORAGE_KEY, JSON.stringify(state))
    return () => localStorage.removeItem(AUTH_STATE_STORAGE_KEY)
  }, [state])

  const login = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: state.redirectUri,
    state: JSON.stringify(state),
    scope: scope ? scope.join(' ') : undefined,
  })

  return (
    <Button
      className="flex w-full"
      variant="outline"
      size="lg"
      {...buttonProps}
      onClick={() => {
        // Login has its own auth events; only the connect flows feed the connection funnel.
        if (flow === 'youtube') trackPlatformConnectStarted('youtube')
        if (flow === 'drive') trackConnectorConnectStarted('google_drive')
        login()
      }}>
      {buttonProps?.icon === null ? null : buttonProps?.icon ? (
        buttonProps.icon
      ) : (
        <GoogleLogo width="20" height="20" />
      )}
      {buttonProps?.children ?? 'Login with Google'}
    </Button>
  )
}
